/**
 * Behaviour for blocks/pt-mega-menu.liquid: the desktop mega panel
 * (hover-intent open/close, keyboard + focus support, Escape to close) and
 * the mobile drill-down drawer (hamburger toggle, backdrop, root/panel
 * screens, Escape to close).
 * Loaded once globally from layout/theme.liquid, like pt-cart-count.js,
 * because the header renders on every page.
 * @module pt-mega-menu
 */

const OPEN_DELAY = 90;
const CLOSE_DELAY = 220;

/**
 * @param {HTMLElement} nav
 */
function initDesktopNav(nav) {
  /** @type {HTMLElement | null} */
  let openItem = null;
  /** @type {number | undefined} */
  let openTimer;
  /** @type {number | undefined} */
  let closeTimer;
  /** Set when hover intent opened the panel, so the click that usually follows keeps it open. */
  let openedByHover = false;

  /**
   * @param {HTMLElement} item
   * @param {HTMLButtonElement} trigger
   */
  function openPanel(item, trigger) {
    window.clearTimeout(closeTimer);
    if (openItem && openItem !== item) closeNow(openItem);
    item.classList.add('pt-mega-menu__item--open');
    trigger.setAttribute('aria-expanded', 'true');
    openItem = item;
  }

  /**
   * @param {HTMLElement} item
   */
  function closeNow(item) {
    const trigger = item.querySelector('[data-pt-mega-menu-trigger]');
    item.classList.remove('pt-mega-menu__item--open');
    trigger?.setAttribute('aria-expanded', 'false');
    if (openItem === item) {
      openItem = null;
      openedByHover = false;
    }
  }

  nav.querySelectorAll('[data-pt-mega-menu-item]').forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    const trigger = item.querySelector('[data-pt-mega-menu-trigger]');
    if (!(trigger instanceof HTMLButtonElement)) return;

    item.addEventListener('mouseenter', () => {
      window.clearTimeout(closeTimer);
      window.clearTimeout(openTimer);
      openTimer = window.setTimeout(() => {
        openPanel(item, trigger);
        openedByHover = true;
      }, OPEN_DELAY);
    });

    item.addEventListener('mouseleave', () => {
      window.clearTimeout(openTimer);
      closeTimer = window.setTimeout(() => closeNow(item), CLOSE_DELAY);
    });

    // Focus alone doesn't open the panel (disclosure pattern: Enter/Space via
    // the click handler does). Opening on focusin made a mouse click open the
    // panel on mousedown-focus and close it again on click.
    item.addEventListener('focusin', () => {
      window.clearTimeout(closeTimer);
    });

    item.addEventListener('focusout', (event) => {
      const next = /** @type {FocusEvent} */ (event).relatedTarget;
      if (next instanceof Node && item.contains(next)) return;
      closeNow(item);
    });

    trigger.addEventListener('click', () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      if (item.classList.contains('pt-mega-menu__item--open') && !openedByHover) {
        closeNow(item);
      } else {
        openPanel(item, trigger);
      }
      openedByHover = false;
    });
  });

  nav.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openItem) return;
    const item = openItem;
    const trigger = item.querySelector('[data-pt-mega-menu-trigger]');
    closeNow(item);
    if (trigger instanceof HTMLElement) trigger.focus();
  });

  document.addEventListener('click', (event) => {
    if (!openItem) return;
    if (event.target instanceof Node && openItem.contains(event.target)) return;
    closeNow(openItem);
  });
}

/**
 * Full-width drill-down drawer: a root screen plus one panel screen per top
 * item. Drill buttons swap the visible screen and the head bar; closing the
 * drawer always resets it to the root screen.
 * @param {HTMLElement} root
 */
function initMobileDrawer(root) {
  const openButton = root.querySelector('[data-pt-mega-menu-open]');
  const closeButton = root.querySelector('[data-pt-mega-menu-close]');
  const backdrop = root.querySelector('[data-pt-mega-menu-backdrop]');
  const drawer = root.querySelector('.pt-mega-menu__drawer');
  const body = root.querySelector('[data-pt-mega-menu-body]');
  const wordmark = root.querySelector('[data-pt-mega-menu-wordmark]');
  const backButton = root.querySelector('[data-pt-mega-menu-back]');
  const title = root.querySelector('[data-pt-mega-menu-title]');
  const rootScreen = root.querySelector('[data-pt-mega-menu-screen="root"]');

  /** The drill button that opened the current panel; null on the root screen. @type {HTMLElement | null} */
  let activeTrigger = null;

  /**
   * Shows one screen and hides the others; the head bar follows.
   * @param {HTMLElement} screen
   * @param {boolean} animate
   */
  function setScreen(screen, animate) {
    root.querySelectorAll('[data-pt-mega-menu-screen]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = el !== screen;
    });
    const isPanel = screen !== rootScreen;
    if (wordmark instanceof HTMLElement) wordmark.hidden = isPanel;
    if (backButton instanceof HTMLElement) backButton.hidden = !isPanel;
    if (title instanceof HTMLElement) {
      title.hidden = !isPanel;
      title.textContent = isPanel ? screen.dataset.title || '' : '';
    }
    if (body) body.scrollTop = 0;
    if (!animate) return;
    screen.classList.add('pt-mega-menu__screen--entering');
    // animationend never fires when the animation is off (reduced motion).
    const clear = () => screen.classList.remove('pt-mega-menu__screen--entering');
    screen.addEventListener('animationend', clear, { once: true });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) window.requestAnimationFrame(clear);
  }

  /**
   * @param {HTMLElement} trigger
   */
  function showPanel(trigger) {
    const panel = document.getElementById(trigger.getAttribute('aria-controls') || '');
    if (!panel) return;
    activeTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    setScreen(panel, true);
    if (backButton instanceof HTMLElement) backButton.focus();
  }

  /**
   * @param {boolean} restoreFocus - Focus the drill button that opened the panel.
   */
  function showRoot(restoreFocus) {
    const trigger = activeTrigger;
    activeTrigger = null;
    root.querySelectorAll('[data-pt-mega-menu-drill]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    if (rootScreen instanceof HTMLElement) setScreen(rootScreen, false);
    if (restoreFocus && trigger) trigger.focus();
  }

  function open() {
    window.clearTimeout(closingTimer);
    root.classList.remove('pt-mega-menu__mobile--closing');
    root.classList.add('pt-mega-menu__mobile--open');
    document.body.classList.add('pt-mega-menu-open');
    openButton?.setAttribute('aria-expanded', 'true');
    if (closeButton instanceof HTMLElement) closeButton.focus();
  }

  const picker = root.querySelector('dropdown-localization-component');

  /** Collapses the country picker (Horizon's own `hidePanel`) if it is open. */
  function closePicker() {
    if (picker && typeof picker.hidePanel === 'function' && !picker.isHidden) {
      picker.hidePanel();
      return true;
    }
    return false;
  }

  /** @type {number | undefined} */
  let closingTimer;

  function close() {
    closePicker();
    showRoot(false);
    // Keeps the header's `contain` relaxed until the slide-out ends, or the
    // fixed drawer shrinks to the header row while it animates (pt-home.css).
    root.classList.add('pt-mega-menu__mobile--closing');
    window.clearTimeout(closingTimer);
    closingTimer = window.setTimeout(() => root.classList.remove('pt-mega-menu__mobile--closing'), 300);
    root.classList.remove('pt-mega-menu__mobile--open');
    document.body.classList.remove('pt-mega-menu-open');
    openButton?.setAttribute('aria-expanded', 'false');
    if (openButton instanceof HTMLElement) openButton.focus();
  }

  openButton?.addEventListener('click', open);
  closeButton?.addEventListener('click', close);
  backdrop?.addEventListener('click', close);
  backButton?.addEventListener('click', () => showRoot(true));
  root.querySelectorAll('[data-pt-mega-menu-drill]').forEach((trigger) => {
    if (trigger instanceof HTMLElement) trigger.addEventListener('click', () => showPanel(trigger));
  });

  root.addEventListener('keydown', (event) => {
    if (!root.classList.contains('pt-mega-menu__mobile--open')) return;
    if (event.key === 'Escape') {
      // First Escape only folds the country picker back up. Focus was inside
      // the panel that just hid, so hand it back to the picker button (Horizon's
      // own keyup handler that would do this is removed by hidePanel).
      if (closePicker()) {
        const pickerButton = picker?.querySelector('.dropdown-localization__button');
        if (pickerButton instanceof HTMLElement) pickerButton.focus();
      } else {
        close();
      }
      return;
    }
    // Keep Tab inside the drawer while it is open (it is an aria-modal dialog).
    // Hidden screens have no layout box, so the filter drops them.
    if (event.key !== 'Tab' || !drawer) return;
    const focusable = [
      ...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), summary'),
    ].filter((el) => el instanceof HTMLElement && el.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!(first instanceof HTMLElement) || !(last instanceof HTMLElement)) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

document.querySelectorAll('[data-pt-mega-menu-nav]').forEach((nav) => {
  if (nav instanceof HTMLElement) initDesktopNav(nav);
});

document.querySelectorAll('[data-pt-mega-menu-mobile]').forEach((root) => {
  if (root instanceof HTMLElement) initMobileDrawer(root);
});
